import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Building2, Send as SendIcon, ArrowDownToLine, Copy, ShieldCheck, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, Spinner } from "../components/common";

function copy(text, label) {
  navigator.clipboard?.writeText(text);
  toast.success(`${label} copied`);
}

function SetupCard({ onOnboard, busy, treasuryStatus }) {
  return (
    <div data-testid="treasury-setup-card" className="rounded-3xl bg-card border border-border p-6 text-center">
      <span className="grid place-items-center w-16 h-16 rounded-2xl bg-warning/10 text-warning mx-auto mb-4"><Building2 size={30} /></span>
      <h3 className="font-display text-xl font-semibold">Business account — setup required</h3>
      <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
        A real account & routing number is powered by Stripe Treasury. It needs Treasury-for-platforms approval
        and an active treasury capability on your Stripe account. No account is created and no money moves until then.
      </p>
      <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-secondary text-muted-foreground mt-4">
        Treasury status: <span className="uppercase">{treasuryStatus || "unrequested"}</span>
      </div>
      <button data-testid="treasury-onboard-btn" onClick={onOnboard} disabled={busy}
        className="w-full h-13 mt-5 py-3 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Spinner className="w-5 h-5" /> : "Start Stripe onboarding"}
      </button>
    </div>
  );
}

function ProvisionCard({ onProvision, busy }) {
  return (
    <div data-testid="treasury-provision-card" className="rounded-3xl bg-card border border-border p-6 text-center">
      <span className="grid place-items-center w-16 h-16 rounded-2xl bg-success/10 text-success mx-auto mb-4"><ShieldCheck size={30} /></span>
      <h3 className="font-display text-xl font-semibold">Treasury is active</h3>
      <p className="text-sm text-muted-foreground mt-2">Create your Jack Cash business account to get an account & routing number.</p>
      <button data-testid="treasury-provision-btn" onClick={onProvision} disabled={busy}
        className="w-full h-13 mt-5 py-3 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Spinner className="w-5 h-5" /> : "Create business account"}
      </button>
    </div>
  );
}

function SendToBank({ onSent }) {
  const [form, setForm] = useState({ amount: "", name: "", routing_number: "", account_number: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter an amount");
    if (!form.name || form.routing_number.length !== 9 || form.account_number.length < 4)
      return toast.error("Enter valid bank details");
    if (!window.confirm(`Send $${amount.toFixed(2)} to ${form.name}'s bank account?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/treasury/outbound-payment", {
        amount, name: form.name, routing_number: form.routing_number, account_number: form.account_number,
      });
      toast.success(`Payment ${data.status} · ${data.amount_display}`);
      setForm({ amount: "", name: "", routing_number: "", account_number: "" });
      onSent();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const input = "w-full h-12 px-4 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm";
  return (
    <div className="rounded-3xl bg-card border border-border p-5 space-y-3" data-testid="treasury-send-card">
      <div className="flex items-center gap-2"><SendIcon size={18} className="text-primary" /><span className="font-display font-semibold">Send to a bank account</span></div>
      <input data-testid="ob-name" placeholder="Recipient name" value={form.name} onChange={set("name")} className={input} />
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
        <input data-testid="ob-amount" type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={set("amount")} className={input + " pl-8"} />
      </div>
      <input data-testid="ob-routing" placeholder="Routing number (9 digits)" value={form.routing_number} onChange={set("routing_number")} className={input} />
      <input data-testid="ob-account" placeholder="Account number" value={form.account_number} onChange={set("account_number")} className={input} />
      <button data-testid="ob-submit" onClick={submit} disabled={busy}
        className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Spinner className="w-5 h-5" /> : "Send payment"}
      </button>
    </div>
  );
}

export default function Banking() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [movements, setMovements] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/treasury/status");
      setStatus(data);
      if (data.has_financial_account) {
        try { const m = await api.get("/treasury/movements"); setMovements(m.data); } catch { /* ignore */ }
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/treasury/onboard", { origin_url: window.location.origin });
      window.location.href = data.onboarding_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const provision = async () => {
    setBusy(true);
    try { await api.post("/treasury/provision"); toast.success("Business account created"); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (loading || !status) return <FullLoader />;

  const fa = status.financial_account;
  const aba = fa?.financial_addresses?.find((a) => a.routing_number) || fa?.financial_addresses?.[0];

  return (
    <div className="animate-fade-up">
      <PageHeader title="Business Banking" subtitle="Your Stripe Treasury account" />

      {!status.ready && <SetupCard onOnboard={onboard} busy={busy} treasuryStatus={status.treasury_status} />}
      {status.ready && !status.has_financial_account && <ProvisionCard onProvision={provision} busy={busy} />}

      {status.ready && fa && (
        <div className="space-y-5">
          <div className="rounded-3xl p-6 text-white shadow-[0_18px_44px_rgba(75,35,204,0.3)]"
            style={{ background: "linear-gradient(135deg,#6B3BF5,#4B23CC)" }}>
            <p className="text-white/70 text-xs tracking-[0.18em] uppercase font-semibold flex items-center gap-2"><Landmark size={14} /> Available balance</p>
            <div data-testid="treasury-balance" className="amount-figure text-4xl mt-1">{fa.balance_display}</div>
            <span className="inline-block mt-3 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-white/15">{fa.status}</span>
          </div>

          <div className="rounded-3xl bg-card border border-border p-5" data-testid="treasury-account-details">
            <div className="font-display font-semibold mb-3 flex items-center gap-2"><Building2 size={18} className="text-primary" /> Account details</div>
            {aba ? (
              <div className="space-y-3">
                {aba.bank_name && <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Bank</span><span className="text-sm font-medium">{aba.bank_name}</span></div>}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Routing number</span>
                  <button onClick={() => copy(aba.routing_number, "Routing number")} className="text-sm font-mono font-medium flex items-center gap-1.5">{aba.routing_number} <Copy size={13} /></button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Account number</span>
                  <button onClick={() => copy(aba.account_number || `••••${aba.account_number_last4}`, "Account number")} className="text-sm font-mono font-medium flex items-center gap-1.5">
                    {aba.account_number || `••••${aba.account_number_last4}`} <Copy size={13} />
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Account & routing numbers are being generated. Check back shortly.</p>
            )}
          </div>

          <SendToBank onSent={load} />

          {movements.length > 0 && (
            <div className="rounded-3xl bg-card border border-border p-5" data-testid="treasury-movements">
              <div className="font-display font-semibold mb-3 flex items-center gap-2"><ArrowDownToLine size={18} className="text-primary" /> Recent movements</div>
              <div className="divide-y divide-border">
                {movements.map((m) => (
                  <div key={m.stripe_id} className="flex items-center justify-between py-2.5 text-sm">
                    <div>
                      <div className="font-medium capitalize">{String(m.kind || "").replace(/_/g, " ")}</div>
                      <div className="text-xs text-muted-foreground capitalize">{m.status}</div>
                    </div>
                    <span className="font-mono font-medium">${((m.amount || 0) / 100).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
